import type {
  Body,
  BodyUUID,
  Quaternion,
  Site,
  SiteUUID,
  Vector3,
  WorldBody,
} from "../../types/scene";
import * as THREE from "three";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { create } from "mutative";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { logger } from "../../utils/logger";
import type { SceneStoreGetState, SceneStoreSetState } from "../sceneStore.types";
import type { BodyActions } from "./actionTypes";
import { withHistory } from "./withHistory";
import {
  generateUniqueWorldBodyName,
  isAttachmentSiteName,
  type RuntimeRegistryMap,
  type SceneGraphNode,
  type WorldBodyRef,
} from "./bodyActionUtils";

export const createBodyAttachmentActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): Pick<BodyActions, "findAttachmentSites" | "attachBySites"> => ({
  // Find all attachment sites (or sites with a specific name) in the scene
  findAttachmentSites: (siteName: string = "attachment_site") => {
    const state = get();
    const { bodies, sites, worldBodies } = state;

    // Import the type from actionTypes to ensure consistency
    type AttachmentSiteInfoLocal = {
      siteUuid: SiteUUID;
      siteName: string;
      bodyUuid: BodyUUID;
      bodyName: string;
      worldBodyUuid: BodyUUID;
      worldBodyName: string;
      position: Vector3;
      orientation: Quaternion;
    };

    const results: AttachmentSiteInfoLocal[] = [];

    // Helper to find which worldBody (root) a body belongs to
    // targetBodyKey is the key in the bodies map (which might differ from body.uuid)
    const findWorldBodyForBody = (
      targetBodyKey: BodyUUID
    ): { uuid: BodyUUID; name: string } | null => {
      const targetBody = bodies[targetBodyKey];

      // Helper to get the display name from a WorldBody entry
      // WorldBody can be either a string (UUID) or an object { uuid, name }
      // We want to prioritize the WorldBody.name (display name like "Object 1") over the body's name
      const getWorldBodyDisplayName = (wb: WorldBodyRef, bodyKey: BodyUUID): string => {
        if (typeof wb === "object" && wb.name) {
          // WorldBody object has a display name - use it
          return wb.name;
        }
        // Fallback: use the body's name from the bodies map
        const body = bodies[bodyKey];
        return body?.name || bodyKey;
      };

      // Check if this body is itself a world body (compare against map key AND body.uuid)
      const worldBody = (worldBodies as WorldBodyRef[]).find((wb) => {
        const wbUuid = typeof wb === "string" ? wb : wb.uuid;
        return wbUuid === targetBodyKey || (targetBody && wbUuid === targetBody.uuid);
      });
      if (worldBody) {
        // Return the map key as uuid (not worldBody.uuid) since that's what we use to look up bodies
        const wbName = getWorldBodyDisplayName(worldBody, targetBodyKey);
        return { uuid: targetBodyKey, name: wbName };
      }

      // Otherwise, walk up the tree to find the root
      const visited = new Set<string>();
      let currentKey = targetBodyKey;

      while (currentKey && !visited.has(currentKey)) {
        visited.add(currentKey);

        const currentBody = bodies[currentKey];

        // Check if current is a world body
        const wb = (worldBodies as WorldBodyRef[]).find((wb) => {
          const wbUuid = typeof wb === "string" ? wb : wb.uuid;
          return wbUuid === currentKey || (currentBody && wbUuid === currentBody.uuid);
        });
        if (wb) {
          const wbName = getWorldBodyDisplayName(wb, currentKey);
          return { uuid: currentKey, name: wbName };
        }

        // Find parent by checking which body has this as a child
        let parentKey: string | null = null;
        for (const [key, body] of Object.entries(bodies)) {
          if (body && Array.isArray(body.children) && body.children.length > 0) {
            const hasChild = body.children.some(
              (childRef) =>
                childRef === currentKey || (currentBody && childRef === currentBody.uuid)
            );
            if (hasChild) {
              parentKey = key;
              break;
            }
          }
        }

        if (!parentKey) break;
        currentKey = parentKey as BodyUUID;
      }

      return null;
    };

    const matchesSiteName = (name: string | undefined | null): boolean =>
      siteName === "attachment_site" ? isAttachmentSiteName(name) : name === siteName;

    // Iterate through all bodies to find sites with matching name
    for (const [bodyUuid, body] of Object.entries(bodies)) {
      if (!body || !Array.isArray(body.sites)) continue;

      for (const siteRef of body.sites) {
        const siteUuid = typeof siteRef === "string" ? siteRef : (siteRef as Site).uuid;
        const site = sites[siteUuid];

        if (site && matchesSiteName(site.name)) {
          const worldBodyInfo = findWorldBodyForBody(bodyUuid as BodyUUID);
          if (worldBodyInfo) {
            // IMPORTANT: Use the bodyUuid (map key) as the worldBodyUuid if it matches the worldBody
            // This ensures we use the actual key from the bodies map, not potentially mismatched UUID
            const isRootBody = (worldBodies as WorldBodyRef[]).some((wb) => {
              const wbUuid = typeof wb === "string" ? wb : wb.uuid;
              return wbUuid === bodyUuid;
            });

            results.push({
              siteUuid: site.uuid as SiteUUID,
              siteName: site.name,
              bodyUuid: bodyUuid as BodyUUID,
              bodyName: body.name,
              // Use the map key for root bodies, otherwise use the worldBodyInfo.uuid
              worldBodyUuid: isRootBody ? (bodyUuid as BodyUUID) : worldBodyInfo.uuid,
              worldBodyName: worldBodyInfo.name,
              position: site.position,
              orientation: site.orientation,
            });
          }
        }
      }
    }

    return results;
  },

  // Attach a gripper/tool to a robot by aligning their attachment sites
  attachBySites: withHistory(
    (
      gripperWorldBodyUuid: BodyUUID,
      robotWorldBodyUuid: BodyUUID,
      siteName: string = "attachment_site",
      gripperSiteUuid?: SiteUUID,
      robotSiteUuid?: SiteUUID
    ): boolean => {
      const state = get();
      const { bodies, sites } = state;

      // In some legacy/import paths, the `bodies` record key can differ from `body.uuid`.
      // Normalize incoming "uuid" to the actual key we must use for `bodies[...]` lookups.
      const resolveBodyKey = (maybeUuid: BodyUUID): BodyUUID | null => {
        if (bodies[maybeUuid]) return maybeUuid;
        const found = Object.entries(bodies).find(([, b]) => b?.uuid === maybeUuid);
        return found ? (found[0] as BodyUUID) : null;
      };
      const gripperRootKey = resolveBodyKey(gripperWorldBodyUuid);
      const robotRootKey = resolveBodyKey(robotWorldBodyUuid);
      if (!gripperRootKey) {
        logger.error(`[attachBySites] Gripper world body not found: ${gripperWorldBodyUuid}`);
        return false;
      }
      if (!robotRootKey) {
        logger.error(`[attachBySites] Robot world body not found: ${robotWorldBodyUuid}`);
        return false;
      }

      // Find attachment site in the robot
      let robotSiteBodyUuid: BodyUUID | null = null;

      // Helper to find site within a body tree
      const bodyLookup = bodies as unknown as Record<string, Body | undefined>;
      const findSiteInBodyTree = (
        rootKey: BodyUUID,
        targetSiteName: string,
        options: { flexibleAttachmentName?: boolean } = {}
      ): { site: Site; bodyUuid: BodyUUID } | null => {
        const visited = new Set<string>();
        const queue: string[] = [rootKey];

        // Also try to find the body by uuid if direct key lookup fails
        if (!bodyLookup[rootKey]) {
          const foundEntry = Object.entries(bodyLookup).find(([, b]) => b?.uuid === rootKey);
          if (foundEntry) {
            queue[0] = foundEntry[0];
          }
        }

        while (queue.length > 0) {
          const currentKey = queue.shift()!;
          if (visited.has(currentKey)) continue;
          visited.add(currentKey);

          let body = bodyLookup[currentKey];
          let actualKey = currentKey;

          // If not found by key, try to find by uuid
          if (!body) {
            const foundEntry = Object.entries(bodyLookup).find(([, b]) => b?.uuid === currentKey);
            if (foundEntry) {
              body = foundEntry[1];
              actualKey = foundEntry[0];
              visited.add(actualKey);
            }
          }

          if (!body) continue;

          // Check sites on this body
          if (Array.isArray(body.sites)) {
            for (const siteRef of body.sites) {
              const siteUuid = typeof siteRef === "string" ? siteRef : (siteRef as Site).uuid;
              const site = sites[siteUuid];
              const siteMatches =
                options.flexibleAttachmentName && targetSiteName === "attachment_site"
                  ? isAttachmentSiteName(site?.name)
                  : site?.name === targetSiteName;
              if (site && siteMatches) {
                return { site, bodyUuid: actualKey as BodyUUID };
              }
            }
          }

          // Add children to queue
          if (Array.isArray(body.children)) {
            queue.push(...body.children);
          }
        }

        return null;
      };

      const findBodyForSiteUuid = (targetSiteUuid: SiteUUID): BodyUUID | null => {
        for (const [bodyUuid, body] of Object.entries(bodies)) {
          if (!body || !Array.isArray(body.sites)) continue;
          for (const siteRef of body.sites) {
            const siteUuid = typeof siteRef === "string" ? siteRef : (siteRef as Site).uuid;
            if (siteUuid === targetSiteUuid) return bodyUuid as BodyUUID;
          }
        }
        return null;
      };

      const resolveExplicitSite = (
        targetSiteUuid?: SiteUUID
      ): { site: Site; bodyUuid: BodyUUID } | null => {
        if (!targetSiteUuid) return null;
        const site = sites[targetSiteUuid];
        const bodyUuid = findBodyForSiteUuid(targetSiteUuid);
        if (!site || !bodyUuid) return null;
        return { site, bodyUuid };
      };

      // Find sites in gripper and robot
      const gripperResult =
        resolveExplicitSite(gripperSiteUuid) || findSiteInBodyTree(gripperRootKey, siteName);
      const robotResult =
        resolveExplicitSite(robotSiteUuid) ||
        findSiteInBodyTree(robotRootKey, siteName, { flexibleAttachmentName: true });

      if (!gripperResult) {
        const siteDetail = gripperSiteUuid ? `site uuid ${gripperSiteUuid}` : `"${siteName}"`;
        logger.error(
          `[attachBySites] No ${siteDetail} site found in gripper (world body: ${gripperWorldBodyUuid})`
        );
        return false;
      }
      if (!robotResult) {
        const siteDetail = robotSiteUuid ? `site uuid ${robotSiteUuid}` : `"${siteName}"`;
        logger.error(
          `[attachBySites] No ${siteDetail} site found in robot (world body: ${robotWorldBodyUuid})`
        );
        return false;
      }

      const gripperSite = gripperResult.site;
      const robotSite = robotResult.site;
      robotSiteBodyUuid = robotResult.bodyUuid;

      // Get the gripper's root body
      const gripperRootBody = bodies[gripperRootKey];
      if (!gripperRootBody) {
        logger.error(`[attachBySites] Gripper root body not found: ${gripperRootKey}`);
        return false;
      }

      const gripperSiteBodyUuid = gripperResult.bodyUuid;

      // Prevent creating cycles in the body hierarchy (which can create cycles in the Three.js scene graph
      // and cause Object3D.traverse() to overflow the stack).
      // Cycle occurs if we try to parent `gripperWorldBodyUuid` under a body that is already a descendant of it.
      const isDescendant = (
        ancestorUuid: BodyUUID,
        potentialDescendantUuid: BodyUUID,
        visited = new Set<BodyUUID>()
      ): boolean => {
        if (ancestorUuid === potentialDescendantUuid) return true;
        if (visited.has(ancestorUuid)) return false;
        visited.add(ancestorUuid);
        const ancestor = bodies[ancestorUuid];
        const children = ancestor && Array.isArray(ancestor.children) ? ancestor.children : [];
        for (const childUuid of children) {
          if (!childUuid) continue;
          if (childUuid === potentialDescendantUuid) return true;
          if (isDescendant(childUuid as BodyUUID, potentialDescendantUuid, visited)) return true;
        }
        return false;
      };

      if (robotSiteBodyUuid === gripperRootKey) {
        logger.error(
          `[Attachment] Cannot attach body "${gripperRootBody.name}" to itself (uuid: ${gripperRootKey}).`
        );
        return false;
      }

      if (isDescendant(gripperRootKey, robotSiteBodyUuid!)) {
        logger.error(
          `[Attachment] Cannot attach "${gripperRootBody.name}" under "${bodies[robotSiteBodyUuid!]?.name}". ` +
            `The target body is already inside the gripper/tool hierarchy, which would create a circular reference.`
        );
        return false;
      }

      const getObject = (nameOrUuid: string): THREE.Object3D | null => {
        try {
          const reg = (RuntimeRegistry.getAll() || {}) as RuntimeRegistryMap;
          const direct = reg[nameOrUuid];
          if (direct) return direct;
          for (const key of Object.keys(reg)) {
            const obj = reg[key];
            if (obj?.userData?.bodyUuid === nameOrUuid) return obj;
            if (key === nameOrUuid && obj) return obj;
          }
          return null;
        } catch {
          return null;
        }
      };

      const getWorldMatrixFromRuntime = (bodyUuid: BodyUUID): THREE.Matrix4 | null => {
        const obj = getObject(bodyUuid);
        if (!obj) return null;
        obj.updateMatrixWorld(true);
        return obj.matrixWorld.clone();
      };

      const composeLocalMatrix = (pos: Vector3, quat: Quaternion): THREE.Matrix4 => {
        const position = new THREE.Vector3(pos[0], pos[1], pos[2]);
        const orientation = new THREE.Quaternion(quat[1], quat[2], quat[3], quat[0]).normalize();
        return new THREE.Matrix4().compose(position, orientation, new THREE.Vector3(1, 1, 1));
      };

      const parentMap = new Map<BodyUUID, BodyUUID>();
      for (const [uuid, body] of Object.entries(bodies) as [string, Body][]) {
        if (!body || !Array.isArray(body.children)) continue;
        for (const childUuid of body.children) {
          if (childUuid) parentMap.set(childUuid as BodyUUID, uuid as BodyUUID);
        }
      }

      const worldMatrixCache = new Map<BodyUUID, THREE.Matrix4>();
      const computeWorldMatrixFromStore = (
        bodyUuid: BodyUUID,
        visiting = new Set<BodyUUID>()
      ): THREE.Matrix4 | null => {
        if (worldMatrixCache.has(bodyUuid)) return worldMatrixCache.get(bodyUuid)!.clone();
        const body = bodies[bodyUuid];
        if (!body) return null;
        if (visiting.has(bodyUuid)) {
          logger.warn(`[Attachment] Circular body reference detected at ${bodyUuid}`);
          return null;
        }
        visiting.add(bodyUuid);
        const parentUuid = parentMap.get(bodyUuid) || null;
        const parentWorld = parentUuid
          ? computeWorldMatrixFromStore(parentUuid, visiting)
          : new THREE.Matrix4();
        if (!parentWorld) {
          visiting.delete(bodyUuid);
          return null;
        }
        const localMat = composeLocalMatrix(body.position, body.orientation);
        const worldMat = parentWorld.clone().multiply(localMat);
        worldMatrixCache.set(bodyUuid, worldMat.clone());
        visiting.delete(bodyUuid);
        return worldMat;
      };

      const runtimeRobotBodyWorld = getWorldMatrixFromRuntime(robotSiteBodyUuid!);
      const runtimeGripperSiteBodyWorld = getWorldMatrixFromRuntime(gripperSiteBodyUuid);
      const runtimeGripperRootWorld = getWorldMatrixFromRuntime(gripperRootKey);

      const useRuntime =
        runtimeRobotBodyWorld && runtimeGripperSiteBodyWorld && runtimeGripperRootWorld;

      if (!useRuntime) {
        logger.warn(
          "[Attachment] Runtime body transforms unavailable, falling back to store transforms."
        );
      }

      const robotBodyWorld = useRuntime
        ? runtimeRobotBodyWorld!
        : computeWorldMatrixFromStore(robotSiteBodyUuid!);
      const gripperSiteBodyWorld = useRuntime
        ? runtimeGripperSiteBodyWorld!
        : computeWorldMatrixFromStore(gripperSiteBodyUuid);
      const gripperRootWorld = useRuntime
        ? runtimeGripperRootWorld!
        : computeWorldMatrixFromStore(gripperRootKey);

      if (!robotBodyWorld || !gripperSiteBodyWorld || !gripperRootWorld) {
        logger.error("[Attachment] Missing body transforms for site alignment.");
        return false;
      }

      const scene = getViewerHandles().scene;
      const findSiteGroupByUuid = (targetSiteUuid: SiteUUID): THREE.Object3D | null => {
        if (!scene) return null;
        let found: THREE.Object3D | null = null;
        // Avoid recursive traverse() which can overflow the stack if the scene graph is cyclic.
        const stack: SceneGraphNode[] = [scene];
        const seen = new Set<SceneGraphNode>();
        while (stack.length > 0) {
          const obj = stack.pop();
          if (!obj || seen.has(obj)) continue;
          seen.add(obj);
          if (obj?.userData?.type === "site" && obj?.userData?.siteUuid === targetSiteUuid) {
            // Site groups also tag CSS2D labels with the same `siteUuid`. Their `matrixWorld`
            // is not reliable for rigid alignment (can differ from the actual site frame).
            if (obj.isCSS2DObject) continue;
            found = obj;
            break;
          }
          const children = obj.children;
          if (Array.isArray(children) && children.length > 0) {
            for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
          }
        }
        return found;
      };

      const robotSiteGroup = findSiteGroupByUuid(robotSite.uuid as SiteUUID);
      const gripperSiteGroup = findSiteGroupByUuid(gripperSite.uuid as SiteUUID);

      // Prefer runtime site transforms (most robust, order-independent, matches what user sees)
      // Fall back to store-based math if runtime scene objects are unavailable.
      const newGripperRootPos = new THREE.Vector3();
      const newGripperRootQuat = new THREE.Quaternion();
      let usedRuntimeSites = false;

      try {
        if (robotSiteGroup && gripperSiteGroup) {
          robotSiteGroup.updateMatrixWorld(true);
          gripperSiteGroup.updateMatrixWorld(true);

          const robotSiteWorld = robotSiteGroup.matrixWorld.clone();
          const gripperSiteWorld = gripperSiteGroup.matrixWorld.clone();

          const runtimeGripperRoot = getWorldMatrixFromRuntime(gripperRootKey);
          const runtimeRobotParent = getWorldMatrixFromRuntime(robotSiteBodyUuid!);
          if (runtimeGripperRoot && runtimeRobotParent) {
            // root->site in world coordinates
            const rootToSiteWorld = runtimeGripperRoot.clone().invert().multiply(gripperSiteWorld);
            // desired root world so that (root * rootToSite) == robotSiteWorld
            const desiredRootWorld = robotSiteWorld
              .clone()
              .multiply(rootToSiteWorld.clone().invert());
            // convert to parent-local (robotSiteBody) for storing on the gripper root body
            const desiredRootLocal = runtimeRobotParent.clone().invert().multiply(desiredRootWorld);
            desiredRootLocal.decompose(newGripperRootPos, newGripperRootQuat, new THREE.Vector3());
            usedRuntimeSites = true;
          }
        }
      } catch (error) {
        logger.warn(
          "[Attachment] Failed to compute alignment from runtime site transforms:",
          error
        );
      }

      if (!usedRuntimeSites) {
        // Store-based alignment math (fallback)
        // Note: relative transforms (root^-1 * siteBodyWorld) are frame-invariant, but this path can be
        // less accurate if runtime poses differ from store (e.g. physics running).
        const robotSiteLocal = composeLocalMatrix(robotSite.position, robotSite.orientation);
        const gripperSiteLocal = composeLocalMatrix(gripperSite.position, gripperSite.orientation);
        const rootToSiteBody = gripperRootWorld.clone().invert().multiply(gripperSiteBodyWorld);
        const rootToSiteLocal = rootToSiteBody.clone().multiply(gripperSiteLocal);
        const gripperRootLocal = robotSiteLocal.clone().multiply(rootToSiteLocal.clone().invert());
        gripperRootLocal.decompose(newGripperRootPos, newGripperRootQuat, new THREE.Vector3());
      }

      // (debug logging removed)

      let shouldDispatchStructureChanged = false;
      set((state) =>
        create(state, (draft) => {
          const gripperIds = new Set<string>(
            [
              gripperWorldBodyUuid as string,
              gripperRootKey as string,
              (draft.bodies?.[gripperRootKey]?.uuid as string) || (gripperRootBody?.uuid as string),
            ].filter(Boolean)
          );
          const robotIds = new Set<string>(
            [
              robotWorldBodyUuid as string,
              robotRootKey as string,
              (draft.bodies?.[robotRootKey]?.uuid as string) ||
                (bodies?.[robotRootKey]?.uuid as string),
            ].filter(Boolean)
          );

          // Remove gripper from worldBodies
          draft.worldBodies = draft.worldBodies.filter((wb: WorldBodyRef) => {
            const wbUuid = typeof wb === "string" ? wb : wb.uuid;
            return !gripperIds.has(wbUuid);
          });

          // Ensure the gripper root has only one parent in the flat map:
          // remove it from existing parent children lists before re-parenting.
          for (const [, body] of Object.entries(draft.bodies) as [string, Body][]) {
            if (!body || !Array.isArray(body.children) || body.children.length === 0) continue;
            // Remove all occurrences (defensive)
            for (let i = body.children.length - 1; i >= 0; i--) {
              const childUuid = body.children[i];
              if (gripperIds.has(childUuid)) {
                body.children.splice(i, 1);
              }
            }
          }

          // If the robot root is missing from worldBodies, re-add it.
          // Without a valid world root, `getBodyTree()` returns empty and the viewer rebuild clears the scene.
          const hasRobotRoot = draft.worldBodies.some((wb: WorldBodyRef) => {
            const wbUuid = typeof wb === "string" ? wb : wb.uuid;
            return robotIds.has(wbUuid);
          });
          if (!hasRobotRoot) {
            const bodyName = draft.bodies?.[robotRootKey]?.name || robotRootKey;
            logger.warn(
              `[Attachment] Robot root "${bodyName}" was missing from worldBodies; re-adding to prevent viewer from clearing the scene.`
            );
            // Always generate a unique display name for the restored root
            const name = generateUniqueWorldBodyName(draft.worldBodies as WorldBody[]);
            draft.worldBodies.push({ uuid: robotRootKey as BodyUUID, name });
          }

          // Add gripper root as child of robot site's body
          const robotSiteBody = draft.bodies[robotSiteBodyUuid!];
          if (robotSiteBody) {
            if (!Array.isArray(robotSiteBody.children)) {
              robotSiteBody.children = [];
            }
            if (!robotSiteBody.children.includes(gripperRootKey)) {
              robotSiteBody.children.push(gripperRootKey);
            }
          } else {
            logger.error(`[Attachment] Robot site body not found: ${robotSiteBodyUuid}`);
          }

          // Update gripper root body transform
          const gripperRoot = draft.bodies[gripperRootKey];
          if (gripperRoot) {
            const beforePos = null;
            const beforeQuat = null;
            const newPos: Vector3 = [newGripperRootPos.x, newGripperRootPos.y, newGripperRootPos.z];
            const newQuat: Quaternion = [
              newGripperRootQuat.w,
              newGripperRootQuat.x,
              newGripperRootQuat.y,
              newGripperRootQuat.z,
            ];
            gripperRoot.position = newPos;
            gripperRoot.orientation = newQuat;

            // Keep transform caches in sync.
            // Scene rebuilds preferentially apply `state.bodyPositions/bodyRotations` (if present),
            // which can otherwise override this attachment transform and leave the gripper at the origin.
            if (!draft.bodyPositions) draft.bodyPositions = {};
            if (!draft.bodyRotations) draft.bodyRotations = {};
            const gripperTransformKey = (gripperRoot.uuid as string) || gripperRootKey;
            draft.bodyPositions[gripperTransformKey] = [...newPos];
            draft.bodyRotations[gripperTransformKey] = [...newQuat];
            void beforePos;
            void beforeQuat;
          } else {
            logger.error(`[Attachment] Gripper root body not found: ${gripperRootKey}`);
          }

          // IMPORTANT: don't dispatch inside the mutative update.
          // Scene rebuild listeners may read stale store state if we dispatch before the store commit completes.
          shouldDispatchStructureChanged = true;
        })
      );

      if (shouldDispatchStructureChanged) {
        try {
          window.dispatchEvent(new CustomEvent("structure-changed"));
        } catch {
          // Silently ignore
        }
      }

      // (post-attach debug logging removed)

      logger.debug(
        `[Attachment] Attached "${gripperRootBody.name}" to "${bodies[robotSiteBodyUuid!]?.name}"`
      );
      return true;
    }
  ),
});
